import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { WHATSAPP_URL, PHONE_TEL, PHONE_DISPLAY } from "@/data/content";
import PriceMenu from "./PriceMenu";

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "experience", label: t.nav.experience },
    { id: "gallery", label: t.nav.gallery },
    { id: "reviews", label: t.nav.reviews },
    { id: "pricing", label: t.nav.pricing },
  ];

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-cream/70 backdrop-blur-xl border-b border-ink/10 shadow-[0_8px_32px_rgba(45,42,38,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-serif text-2xl md:text-[1.7rem] tracking-tight text-ink"
        >
          Steve<span className="text-brand italic"> Massage</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-xs tracking-[0.16em] uppercase text-ink-soft hover:text-ink transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4 md:gap-5">
          <PriceMenu />
          <a
            data-testid="nav-phone"
            href={`tel:${PHONE_TEL}`}
            className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.12em] text-ink hover:text-brand transition-colors duration-300"
            aria-label={`Appeler ${PHONE_DISPLAY}`}
          >
            <Phone size={14} strokeWidth={2} />
            {PHONE_DISPLAY}
          </a>
          <div className="flex items-center text-xs tracking-[0.15em] uppercase" data-testid="lang-switch">
            {["fr", "en"].map((l, i) => (
              <span key={l} className="flex items-center">
                {i === 1 && <span className="mx-1.5 text-ink/30">/</span>}
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`transition-colors duration-300 ${
                    lang === l ? "text-ink font-medium" : "text-ink/40 hover:text-ink"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              </span>
            ))}
          </div>
          <a
            data-testid="nav-book-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center rounded-full bg-ink text-cream text-xs tracking-[0.15em] uppercase px-6 py-3 hover:bg-brand transition-colors duration-400"
          >
            {t.nav.book}
          </a>
        </div>
      </div>
    </motion.header>
  );
}
